import React, { useState, useEffect, useCallback, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Form, Button, Spinner, Alert, Badge } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { formatMoney } from "./OrderFinancialSummary";
import { TranslatorContext } from "../../../context/Translator";

export default function WebOrdersListPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [marketplaces, setMarketplaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalOrders, setTotalOrders] = useState(0);

    const [search, setSearch] = useState("");
    const [marketplaceId, setMarketplaceId] = useState("");
    const [deliveryStatus, setDeliveryStatus] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const tableHeaders = [
        "ORDER #",
        "SOURCE",
        "PRODUCT CODES",
        "BUYER NAME",
        "Country",
        "REVENUE",
        "COMPANY PROFIT",
        "DELIVERY STATUS",
        "ITEMS",
        "ORDERED AT",
        "ACTION",
    ];

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/admin/web-orders/initial-data`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
                );

                const list = response.data?.market_places || response.data?.marketplaces || [];
                setMarketplaces(list);
            } catch (err) {
                console.error("Failed to load marketplaces for filter", err);
            }
        };

        fetchInitialData();
    }, []);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {};
            if (search) params.search = search;
            if (marketplaceId) params.marketplace_id = marketplaceId;
            if (deliveryStatus) params.delivery_status = deliveryStatus;
            if (fromDate) params.from_date = fromDate;
            if (toDate) params.to_date = toDate;

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/admin/web-orders`,
                { params, headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
            );

            if (response.data?.success) {
                setOrders(response.data.data || []);
                setTotalOrders(response.data.total_orders || response.data.data?.length || 0);
            } else {
                setOrders([]);
                setTotalOrders(0);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch website orders. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [search, marketplaceId, deliveryStatus, fromDate, toDate]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        fetchOrders();
    };

    const handleResetFilters = () => {
        setSearch("");
        setMarketplaceId("");
        setDeliveryStatus("");
        setFromDate("");
        setToDate("");
    };

    const getMarketplaceName = (item) => {
        if (!item) return "";
        if (typeof item === "string") return item;
        return item.name || item.title || item.marketplace_name || item.label || "";
    };


    const getDeliveryBadge = (status) => {
        switch (status) {
            case "Delivered":
                return <Badge bg="success" className="px-3 py-2 text-uppercase rounded-pill fw-semibold">Delivered</Badge>;
            case "Shipped":
                return <Badge bg="primary" className="px-3 py-2 text-uppercase rounded-pill fw-semibold">Shipped</Badge>;
            case "Pending":
                return <Badge bg="warning" text="dark" className="px-3 py-2 text-uppercase rounded-pill fw-semibold">Pending</Badge>;
            case "Returned":
                return <Badge bg="danger" className="px-3 py-2 text-uppercase rounded-pill fw-semibold">Returned</Badge>;
            default:
                return <Badge bg="secondary" className="px-3 py-2 text-uppercase rounded-pill fw-semibold">{status || "N/A"}</Badge>;
        }
    };

    return (
        <PageLayout>
            <Row className="g-4">
                <Col xl={12}>
                    <div className="mc-card bg-white p-4 rounded-3 shadow-sm border border-light-subtle d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title fw-bold text-dark m-0 fs-4">
                                Website Orders <span className="text-muted fs-6 fw-normal">({totalOrders})</span>
                            </h3>
                        </div>
                        <ul className="mc-breadcrumb-list list-unstyled d-flex align-items-center gap-2 m-0 small text-secondary">
                            <li className="mc-breadcrumb-item">
                                <Link to="#" className="mc-breadcrumb-link text-decoration-none text-muted">
                                    {t("home") || "Home"}
                                </Link>
                            </li>
                            <li className="mc-breadcrumb-item text-muted">/</li>
                            <li className="mc-breadcrumb-item fw-semibold text-primary">Web Orders</li>
                        </ul>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card bg-white p-4 rounded-3 shadow-sm border border-light-subtle">
                        <Form onSubmit={handleFilterSubmit}>
                            <Row className="g-3 align-items-end">
                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary small mb-1">
                                            Search
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            className="shadow-none border-light-subtle py-2 px-3 fs-6"
                                            placeholder="Order # or Buyer..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary small mb-1">
                                            Marketplace
                                        </Form.Label>
                                        <Form.Select
                                            className="shadow-none border-light-subtle py-2 px-3 fs-6"
                                            value={marketplaceId}
                                            onChange={(e) => setMarketplaceId(e.target.value)}
                                        >
                                            <option value="">All Marketplaces</option>
                                            {marketplaces.map((m) => (
                                                <option key={m.id} value={m.id}>
                                                    {getMarketplaceName(m) || `Marketplace #${m.id}`}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>


                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary small mb-1">
                                            Delivery Status
                                        </Form.Label>
                                        <Form.Select
                                            className="shadow-none border-light-subtle py-2 px-3 fs-6"
                                            value={deliveryStatus}
                                            onChange={(e) => setDeliveryStatus(e.target.value)}
                                        >
                                            <option value="">Delivery Statuses</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Shipped">Shipped</option>
                                            <option value="Delivered">Delivered</option>
                                            <option value="Returned">Returned</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary small mb-1">
                                            From Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            className="shadow-none border-light-subtle py-2 px-3 fs-6"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col xl={2} lg={4} md={6} sm={6} xs={12}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold text-uppercase text-secondary small mb-1">
                                            To Date
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            className="shadow-none border-light-subtle py-2 px-3 fs-6"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col xl={12} className="d-flex justify-content-end gap-2 mt-3 pt-2">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="text-uppercase fw-bold px-4 py-2 shadow-sm rounded-2"
                                    >
                                        Filter
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="light"
                                        className="text-uppercase fw-bold px-4 py-2 border border-light-subtle rounded-2 text-secondary"
                                        onClick={handleResetFilters}
                                    >
                                        Reset
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card bg-white rounded-3 shadow-sm border border-light-subtle overflow-hidden">
                        {error && (
                            <div className="p-3">
                                <Alert variant="danger" className="m-0 rounded-2">{error}</Alert>
                            </div>
                        )}

                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-2 text-muted fw-semibold">Loading website orders...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table align-middle text-center table-hover mb-0">
                                    <thead className="bg-primary text-white">
                                        <tr>
                                            {tableHeaders.map((header, index) => (
                                                <th
                                                    key={index}
                                                    className="py-3 px-3 border-0 text-uppercase fw-bold small text-nowrap "
                                                >
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="border-top-0">
                                        {orders.length > 0 ? (
                                            orders.map((item) => (
                                                <tr key={item.id} className="border-bottom border-light-subtle">
                                                    <td className="py-3 px-3">
                                                        <span className="fw-bold text-primary text-decoration-none">
                                                            {item.order_number || `#${item.id}`}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        <Badge
                                                            bg={item.source?.type === "website" ? "info" : "warning"}
                                                            text="dark"
                                                            className="px-3 py-2 text-uppercase fw-bold rounded-pill shadow-sm"
                                                        >
                                                            {item.source?.label || getMarketplaceName(item.marketplace) || "Website"}
                                                        </Badge>
                                                        {item.source?.mplacer && (
                                                            <div className="small text-muted mt-1">{item.source.mplacer}</div>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-3 text-start">
                                                        {(item.product_codes || []).length > 0
                                                            ? item.product_codes.map((code) => (
                                                                <Badge key={code} bg="light" text="dark" className="border me-1 mb-1">{code}</Badge>
                                                            ))
                                                            : <span className="text-muted">-</span>}
                                                    </td>
                                                    <td className="py-3 px-3 text-start fw-medium text-dark text-nowrap">
                                                        {item.buyer?.name || item.buyer_name || "N/A"}
                                                    </td>
                                                    <td className="py-3 px-3 text-start fw-medium text-dark text-nowrap">
                                                        {item.address?.country || item.buyer?.country || "-"}
                                                    </td>
                                                    <td className="py-3 px-3 fw-bold text-dark text-nowrap text-end">
                                                        {formatMoney(item.financial?.revenue_usd ?? item.subtotal, "USD")}
                                                        <br />
                                                        <span className="small text-muted fw-semibold">
                                                            {item.financial?.revenue_pkr ? formatMoney(item.financial.revenue_pkr, "PKR") : "No exchange rate"}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-3 text-nowrap text-end">
                                                        {item.financial?.status === "complete" ? (
                                                            <>
                                                                <span className={`fw-bold ${Number(item.financial.admin_profit_pkr) < 0 ? "text-danger" : "text-success"}`}>
                                                                    {formatMoney(item.financial.admin_profit_pkr, "PKR")}
                                                                </span>
                                                                <br />
                                                                <span className="small text-muted">{formatMoney(item.financial.admin_profit_usd, "USD")}</span>
                                                            </>
                                                        ) : (
                                                            <Badge bg="light" text="dark" className="border">Incomplete</Badge>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-3 text-nowrap">
                                                        {getDeliveryBadge(item.delivery?.status || item.delivery_status)}
                                                    </td>
                                                    <td className="py-3 px-3 fw-semibold text-dark">
                                                        {item.products ? item.products.length : (item.items_count || 0)}
                                                    </td>
                                                    <td className="py-3 px-3 text-muted small text-nowrap">
                                                        {item.dates?.ordered_at
                                                            ? new Date(item.dates.ordered_at).toLocaleDateString()
                                                            : (item.ordered_at ? new Date(item.ordered_at).toLocaleDateString() : "N/A")}
                                                    </td>
                                                    <td className="py-3 px-3 text-nowrap">
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            className="px-3 py-1 fw-bold text-uppercase rounded-2 shadow-sm"
                                                            onClick={() => navigate(`/admin/web-orders/${item.id}/edit`)}
                                                        >
                                                            Edit
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={tableHeaders.length} className="text-center py-5 text-muted fw-semibold">
                                                    No website orders found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}